import { z } from "zod";
import type { Benefit } from "../types/benefit";
import { FETCH_TIMEOUT_MS } from "../config";

const BenefitSchema = z.object({
  categoria: z.string().min(1),
  nome: z.string().min(1),
  descricao: z.string().min(1),
  link: z.string().url(),
  imagem: z.string().url().optional(),
  destaque: z.string().optional().default("Não")
});

async function fetchWithTimeout(url: string, ms: number): Promise<Response> {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(url, { cache: "no-store", signal: ctrl.signal });
    return res;
  } finally {
    clearTimeout(id);
  }
}

async function retry<T>(fn: () => Promise<T>, attempts = 3, baseDelay = 500): Promise<T> {
  let lastErr: any;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      await new Promise((r) => setTimeout(r, baseDelay * Math.pow(2, i)));
    }
  }
  throw lastErr;
}

export async function fetchBenefits(url: string): Promise<Benefit[]> {
  const text = await retry(async () => {
    const res = await fetchWithTimeout(url, FETCH_TIMEOUT_MS);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  }, 3, 700);

  // Parse GViz JSON (Google Sheets) - Remove prefixo /*O_o*/ se existir
  let cleanText = text;
  if (cleanText.startsWith("/*O_o*/")) {
    cleanText = cleanText.substring(7);
  }
  
  const s = cleanText.indexOf("google.visualization.Query.setResponse(");
  const a = cleanText.indexOf("(", s);
  const b = cleanText.lastIndexOf(")");
  const jsonStr = cleanText.slice(a + 1, b);
  const gviz = JSON.parse(jsonStr);
  
  const rows: Array<{ c: Array<{ v: any } | null> }> = gviz.table.rows || [];
  
  // Pular a primeira linha (header) e processar os dados
  const dataRows = rows.slice(1); // Remove header row
  
  const benefits = dataRows.map((r, idx) => {
    // Mapear diretamente pelas posições das colunas
    // [0] = Categoria, [1] = Nome, [2] = Descrição, [3] = Link, [4] = Imagem, [5] = Destaque
    const categoria = r.c?.[0]?.v || "";
    const nome = r.c?.[1]?.v || "";
    const descricao = r.c?.[2]?.v || "";
    const link = r.c?.[3]?.v || "#";
    const imagem = r.c?.[4]?.v || undefined;
    const destaque = r.c?.[5]?.v || "Não";
    
    return {
      categoria: String(categoria).trim(),
      nome: String(nome).trim(),
      descricao: String(descricao).trim(),
      link: String(link).trim(),
      imagem: imagem ? String(imagem).trim() : undefined,
      destaque: String(destaque).trim()
    };
  });

  // Validate with zod
  const valid: Benefit[] = [];
  benefits.forEach((benefit, i) => {
    const parse = BenefitSchema.safeParse(benefit);
    if (parse.success) {
      valid.push(parse.data);
    } else {
      console.warn(`Benefit row ${i + 2} validation failed:`, parse.error.issues);
    }
  });

  return valid;
}